import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { StyledDetailsContainer, StyledContainer, StyledDates, StyledListItem, StyledList } from './DatesDetails.styles';
import moment from 'moment';
import { useLazyQuery } from '@apollo/client';
import { GET_HISTORY } from 'store/queries';
import { shouldShowExpiredOnHoldInfo, checkForExpiredOnHoldBooking } from './DatesDetails.utils';
import FormattedCurrency from 'components/ui/FormattedCurrency';

const DATE_FORMAT = 'Do MMM YYYY [at] HH:mm';

const DatesDetails = ({ dates, bookingReference, bookingStatus }) => {
  const { createTimestamp, expiryTimestamp, voidExpiryDate, confirmedTimestamp, updatedTimestamp, cancelledTimestamp } = dates;
  const [expiredOnHoldInfo, setExpiredOnHoldInfo] = useState(null);

  const [getHistory] = useLazyQuery(GET_HISTORY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.history?.details) {
        const expiredInfo = checkForExpiredOnHoldBooking(data.history.details);
        setExpiredOnHoldInfo(expiredInfo);
      }
    }
  });

  useEffect(() => {
    if (bookingReference && shouldShowExpiredOnHoldInfo(bookingStatus)) {
      getHistory({
        variables: {
          bookingReference,
          page: 0,
          size: 100
        }
      });
    }
  }, [bookingReference, bookingStatus, getHistory]);
  return (
    <StyledDetailsContainer>
      <StyledContainer>
        <StyledDates>
          <FormattedMessage id="notes.dates.salesDates" />:{' '}
          <strong data-testid="sales-dates">{moment(createTimestamp).isValid() ? moment(createTimestamp).format(DATE_FORMAT) : '-'}</strong>
        </StyledDates>
        <StyledDates>
          <FormattedMessage id="notes.dates.expiryDates" />:{' '}
          <strong data-testid="expiry-dates">
            {moment(expiryTimestamp).isValid() ? moment(expiryTimestamp).format(DATE_FORMAT) : '-'}
          </strong>
        </StyledDates>
        <StyledDates>
          <FormattedMessage id="notes.dates.voidExpiryDates" />:{' '}
          <strong data-testid="void-expiry-dates">
            {moment(voidExpiryDate).isValid() ? moment(voidExpiryDate).format(DATE_FORMAT) : '-'}
          </strong>
        </StyledDates>
        <StyledDates>
          <FormattedMessage id="notes.dates.confirmationDates" />:{' '}
          <strong data-testid="confirmation-dates">
            {moment(confirmedTimestamp).isValid() ? moment(confirmedTimestamp).format(DATE_FORMAT) : '-'}
          </strong>
        </StyledDates>
        <StyledDates>
          <FormattedMessage id="notes.dates.latestUpdate" />:{' '}
          <strong data-testid="updated-dates">
            {moment(updatedTimestamp).isValid() ? moment(updatedTimestamp).format(DATE_FORMAT) : '-'}
          </strong>
        </StyledDates>
        <StyledDates>
          <FormattedMessage id="notes.dates.cancelDates" />:{' '}
          <strong data-testid="cancellation-dates">
            {moment(cancelledTimestamp).isValid() ? moment(cancelledTimestamp).format(DATE_FORMAT) : '-'}
          </strong>
        </StyledDates>
        {expiredOnHoldInfo && (
          <>
            <br />
            <StyledDates>
              <strong>
                <FormattedMessage id="notes.dates.expiredOnHold" />:{' '}
              </strong>
            </StyledDates>
            <StyledList>
              <StyledListItem>
                <FormattedMessage id="notes.dates.valueOfAllExpiredTikets" />:{' '}
                <strong>
                  <FormattedCurrency amount={expiredOnHoldInfo.totalTcnValue} currency={expiredOnHoldInfo.currency} />
                </strong>
              </StyledListItem>
              <StyledListItem>
                <FormattedMessage id="notes.dates.valueOfExpiredDeposit" />:{' '}
                <strong>
                  <FormattedCurrency amount={expiredOnHoldInfo.totalDepositValue} currency={expiredOnHoldInfo.currency} />
                </strong>
              </StyledListItem>
              <StyledListItem>
                <FormattedMessage id="notes.dates.balanceAtTimeOfExpiry" />:{' '}
                <strong>
                  <FormattedCurrency amount={expiredOnHoldInfo.balance} currency={expiredOnHoldInfo.currency} />
                </strong>
              </StyledListItem>
            </StyledList>
          </>
        )}
      </StyledContainer>
    </StyledDetailsContainer>
  );
};

DatesDetails.propTypes = {
  dates: PropTypes.shape({
    confirmedTimestamp: PropTypes.string,
    cancelledTimestamp: PropTypes.string,
    expiryTimestamp: PropTypes.string,
    voidExpiryDate: PropTypes.string,
    createTimestamp: PropTypes.string,
    updatedTimestamp: PropTypes.string
  }),
  bookingReference: PropTypes.string,
  bookingStatus: PropTypes.string
};

export default DatesDetails;
